// ─── Contexte de langue FR / EN ───────────────────────────────────────────────
import { createContext, useContext, useState, ReactNode } from 'react';
import { translations, Lang, TranslationKey } from './translations';

// ─────────────────────────────────────────────────────────────────────────────

interface LanguageContextValue {
  lang:      Lang;
  setLang:   (l: Lang) => void;
  t:         (key: TranslationKey, vars?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

// ─────────────────────────────────────────────────────────────────────────────
// Langue courante lisible HORS React (appels API dans lib/, contextes, widgets).
// Même logique de repli que le provider : localStorage puis langue du navigateur.

export function getCurrentLang(): Lang {
  try {
    const saved = localStorage.getItem('cyberhealth_lang');
    if (saved === 'fr' || saved === 'en') return saved;
  } catch { /* localStorage indisponible */ }
  try {
    return navigator.language.startsWith('fr') ? 'fr' : 'en';
  } catch {
    return 'fr';
  }
}

// Synchronise la langue choisie avec le profil backend (best-effort, silencieux)
// pour que les emails transactionnels suivent la langue de l'UI. No-op si déconnecté.
function syncLangToBackend(l: Lang): void {
  let tok: string | null = null;
  try { tok = localStorage.getItem('wezea_token'); } catch { tok = null; }
  if (!tok) return;
  const base = (import.meta as any).env?.VITE_API_URL || '';
  try {
    void fetch(`${base}/auth/profile`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
      body: JSON.stringify({ language: l }),
    }).catch(() => { /* silencieux */ });
  } catch { /* silencieux */ }
}

// ─────────────────────────────────────────────────────────────────────────────

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(() => {
    // Détecter la langue du navigateur au premier chargement
    const saved = localStorage.getItem('cyberhealth_lang') as Lang | null;
    if (saved === 'fr' || saved === 'en') return saved;
    const browser = navigator.language.startsWith('fr') ? 'fr' : 'en';
    return browser;
  });

  const handleSetLang = (l: Lang) => {
    setLang(l);
    try { localStorage.setItem('cyberhealth_lang', l); } catch { /* ignore */ }
    syncLangToBackend(l);
  };

  // Fonction de traduction avec interpolation simple : {variable}
  const t = (key: TranslationKey, vars?: Record<string, string | number>): string => {
    const raw = (translations[lang] as Record<string, string>)[key]
             ?? (translations.fr  as Record<string, string>)[key]
             ?? key;
    if (!vars) return raw;
    return raw.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang: handleSetLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside <LanguageProvider>');
  return ctx;
}
